/* eslint-disable @typescript-eslint/no-dynamic-delete */
// @ts-strict-ignore

/**
 * @module Webinar
 */

import { BehaviorSubject, Subject, Subscription, filter } from "rxjs";

import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { RBEvent } from "rainbow-web-sdk/models/event.model";
import { Webinar, WebinarCloneParams, WebinarEvents, WebinarParticipant, WebinarRB, WebinarSession, WebinarStatus } from "rainbow-web-sdk/models/webinar.model";

import { Service } from "rainbow-web-sdk/services/service";
import { MainService } from "rainbow-web-sdk/services/core/main.service";
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { WebinarConferenceService } from "rainbow-web-sdk/services/webinars/webinarConference.service";
import { WebinarParticipantsService } from "rainbow-web-sdk/services/webinars/webinarParticipants.service";
import { WebinarSpeakersService } from "rainbow-web-sdk/services/webinars/webinarSpeakers.service";
import { BubbleServiceEvents, BubbleServiceRB, BUBBLE_SVC } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import type { ConversationServiceRB } from "rainbow-web-sdk/services/conversation/conversation.service";
import { CONVERSATION_SVC } from "rainbow-web-sdk/services/conversation/conversation.service";
import { differenceInMinutes, format, parse } from "date-fns";

import xmlParser from 'rainbow-web-sdk/utils/xmlParser';
import { BubbleEvents, BubbleRB, BubbleUser, BubbleUserStatus } from "rainbow-web-sdk/models/bubble.model";
import { stringifyError } from "../../utils/stringifyError";
import { wait } from "rainbow-web-sdk";
import { ProfileService } from "../core";
import { FileStorageService } from "../files/fileStorage.service";

export const WEBINAR_SVC = 'WebinarService';

export interface WebinarService {

    speakers: WebinarSpeakersService;

    /**
     * Create an empty webinar instance
     */
    createWebinarInstance(): Webinar;

    /**
     * This async method has to be call to start the webinar service
     */
    start(): Promise<void>;

    /**
     * This async method waits until the webinar service is fully started
     */
    waitUntilStart(): Promise<void>;

    /**
     * This async method has to be call to stop the webinar service
     * @param nowait - when set to true the leave method is call asynchronously without waiting response of rest request,
     *  this mode has to be used when stop method is called in the onUnload browser event (try to send a sessionTerminate message
     *  when browser or tab is closed brutally)
     */
    stop(nowait: boolean): void;

    /**
     * This asynchronous method has to be call when connection is retreive after a connection lost
     */
    reconnect(): void;

    /**
     *  This method permits to observe webinar change
     * @param observer - Webinar change to observe
     */
    subscribe(handler: any, eventNames?: string | string[]): Subscription;

    /**
     * Asynchronous method to create a new webinar on server
     * @param webinar - the webinar to create server side
     */
    createWebinar(webinar: Webinar): Promise<Webinar>;

    /**
     * Asynchronous method to clone a webinar
     * @param webinar - the webinar to clone
     * @param params - The webinar parameters to clone
     * @returns the cloned webinar
     */
    cloneWebinar(webinar: Webinar, params: WebinarCloneParams): Promise<Webinar>;

    /**
     * Asynchronous method to update an existing webinar on server
     * @param webinarData - the webinarData to update on server(should be partial data)
     */
    updateWebinar(webinarData: Webinar): Promise<Webinar>;

    /**
     * Asynchronous method to update webinar avatar on server
     * @param webinar - the webinar to update
     * @param avatarImg - base64 encoded webinar avatar
     */
    updateWebinarAvatar(webinar: Webinar, avatarImg: string): Promise<Webinar>;

    /**
     * Asynchronous method to update webinar logo on server
     * @param webinar - the webinar to update
     * @param logoSrc - base64 encoded webinar logo
     */
    updateWebinarLogo(webinar: Webinar, logoSrc: string): Promise<Webinar>;

    /**
     * Synchronous method to get and order webinars (no server request)
     * @param order - sort order (available values : "name", "date")
     */
    getWebinars(order: string): Webinar[];

    /**
     * Asynchronous method to retreive webinar from server (reentrant ready method)
     * @param webinarId - the identifiant of webinar to get
     */
    getWebinar(webinarId: string): Promise<Webinar>;

    /**
     * Synchronous method to retreive webinar from cache
     * @param webinarId - the identifiant of webinar to get
     */
    getWebinarById(webinarId: string): Webinar | undefined;

    /**
     * Asynchronous method to retreive public webinar informations (this method can be call without authentication)
     * @param openInviteId - the webinar openInviteId
     * @param joinUuid - the webinar joinUuid
     * @param registrationCall - the registrationClickCount for statisitics (optional)
     */
    getPublicWebinarInfo(openInviteId: string, joinUuid: string, registrationCall?: boolean): Promise<any>;

    /**
     * Asynchronous method to update the webinar already in cache with server data
     * @param webinar - the webinar openInviteId
     */
    updateWebinarFromServer(webinar: Webinar): Promise<Webinar>;

    /**
     * Asynchronous method to delete a webinar
     * @param webinarId - the webinarId
     */
    deleteWebinar(webinarId: string): Promise<void>;

    /**
     * Asynchronous method to publish a webinar
     * @param webinar - the webinar
     */
    publishWebinar(webinar: Webinar): Promise<Webinar>;

    /**
     * Asynchronous method to notified webinar moderators
     * @param webinar - the webinar
     */
    notifyModerators(webinar: Webinar): Promise<Webinar>;

    /**
     * Asynchronous method to check if a user has a webinar licence
     * @param userId - the webinarId
     */
    hasWebinarLicence(userId: string): Promise<boolean>;

    /**
     * Asynchronous method to get available licence between a starting and ending dates
     * @param webinarStartDate - the start date
     * @param webinarEndDate - the end datae
     */
    getAvailableLicences(webinarStartDate: string, webinarEndDate: string): Promise<any>;

    /**
     * Method to check if connected user has active or futur webinars
     */
    hasActiveOrFutureWebinar(): boolean;

    /**
     * getWebinarConversation
     * @param webinar - the webinar
     */
    getWebinarTextConversation(webinar: Webinar): Promise<string>;

    /**
     * Asynchronous method to get webinar associated room from room service
     * @param webinar - the webinar to upadte
     * @param practice - when true also get practice room
     */
    getWebinarRooms(webinar: Webinar, practice?: boolean): Promise<void>;

    /**
     * getGlobalAnalytics
     * @param webinar - the webinar
     */
    getGlobalAnalytics(webinar: Webinar): Promise<any>;

    /**
     * getParticipantsAnalytics
     * @param webinar - the webinar
     */
    getParticipantsAnalytics(webinar: Webinar): Promise<any>;

    /**
     * getAttendeesAnalytics
     * @param webinar - the webinar
     */
    getAttendeesAnalytics(webinar: Webinar): Promise<any>;

    /**
     * Compute the webinar color depending its name
     * @param name - the webinar name
     */
    computeWebinarColor(name: string): string;

    started: BehaviorSubject<boolean>;
    conference: WebinarConferenceService;// A cacher
    participants: WebinarParticipantsService; // A cacher
    muteAudioElements: boolean;

    /**
     * Downloads the recording of the given webinar.
     * @param webinar - The webinar whose recording should be downloaded.
     * @returns A promise that resolves when the download is complete.
     */
    downloadRecord(webinar: Webinar): Promise<void>;

    /**
     * Retrieves the list of scheduled webinars within a given time range.
     * @param startDate - The start of the time range as an ISO 8601 string.
     * @param endDate - The end of the time range as an ISO 8601 string.
     * @returns A promise that resolves to the list of webinars scheduled in the given range.
     */
    getScheduledWebinars(startDate: string, endDate: string): Promise<any[]>;

    /**
     * Retrieves all webinar licences available for the current user.
     * @returns A promise that resolves to the list of licences, each containing a `totalLicenses` count.
     */
    getAllLicences(): Promise<any[]>;

}

/**
 * Event published by the WebinarService.
 */
export enum WebinarServiceEvents {
    /**
     * This RB event is send when the webinars list has changed.
     * @eventProperty WEBINARS_UPDATE
     */
    WEBINARS_UPDATE = 'WEBINARS_UPDATE',

    /**
     * This RB event is send when the webinar is published
     * @eventProperty WEBINAR_PUBLISHED
     */
    WEBINAR_PUBLISHED = 'WEBINAR_PUBLISHED',

    /**
     * This RB event is send when the webinar is terminated
     * @eventProperty WEBINAR_TERMINATED
     */
    WEBINAR_TERMINATED = 'WEBINAR_TERMINATED',

    /**
     * This RB event is send when the webinar is terminated
     * @eventProperty WEBINAR_CONVERSATION_RELOADED
     */
    WEBINAR_CONVERSATION_RELOADED = 'WEBINAR_CONVERSATION_RELOADED',

    /**
     * This RB event is send when an unmute demand is received
     * @eventProperty WEBINAR_UNMUTE_DEMAND_RECEIVED
     */
    WEBINAR_UNMUTE_DEMAND_RECEIVED = 'WEBINAR_UNMUTE_DEMAND_RECEIVED',

    /**
     * This RB event is send when a webinar shared a screen
     * @eventProperty WEBINAR_SHARED_SCREEN
     */
    WEBINAR_SHARED_SCREEN = 'WEBINAR_SHARED_SCREEN',

    /**
     * This RB event is send when a webinar emits a notification
     * @eventProperty WEBINAR_NOTIFICATION
     */
    WEBINAR_NOTIFICATION = 'WEBINAR_NOTIFICATION',

    WEBINAR_REQUEST_STAGING = 'WEBINAR_REQUEST_STAGING',
    WEBINAR_REFUSE_STAGING = 'WEBINAR_REFUSE_STAGING',
    WEBINAR_REQUEST_ATTENDEE_STAGING = 'WEBINAR_REQUEST_ATTENDEE_STAGING',
    WEBINAR_REQUEST_ATTENDEE_UNSTAGING = 'WEBINAR_REQUEST_ATTENDEE_UNSTAGING'
}

export class WebinarServiceRB extends Service implements WebinarService {
    private readonly profileService: ProfileService = ProfileService.getInstance();
    private readonly mainService: MainService = MainService.getInstance();
    private readonly logger: LoggerService = LoggerService.getInstance();
    private readonly authService: AuthService = AuthService.getInstance();
    private readonly contactService: ContactService = ContactService.getInstance();
    private readonly xmppService: XmppService = XmppService.getInstance();
    private readonly errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private readonly fileStorageService: FileStorageService = FileStorageService.getInstance();
    public readonly conversationService: ConversationServiceRB = this.mainService.inject<ConversationServiceRB>(CONVERSATION_SVC);
    public bubbleService: BubbleServiceRB;
    public centralizedService?: any;
    public browserReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private subject: Subject<RBEvent> = new Subject<RBEvent>();

    private webinars: Webinar[] = [];
    private webinarsMap: Record<string, Webinar> = {};
    private subscription: Subscription | null = null;
    private xmppHandlers: any[] = [];
    private createInProgress: boolean = false;
    private getWebinarPromise: Record<string, Promise<Webinar>> = {};

    public conference: WebinarConferenceService;
    public speakers: WebinarSpeakersService;
    public participants: WebinarParticipantsService;

    public userContact: Contact | null = null;
    public guestMode: boolean = true;
    public muteAudioElements: boolean = false;

    public started: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * This the webinarService singleton
     */
    public static getInstance() {
        return Service.create(WEBINAR_SVC, WebinarServiceRB);
    }

    public static build() {
        return new WebinarServiceRB();
    }

    private constructor() {
        super();
        localStorage.setItem('enableWebinarSimulcast', 'false');
        this.speakers = WebinarSpeakersService.create(this);
        this.participants = WebinarParticipantsService.create(this);
    }

    /**
     * This async method has to be call to start the webinar service
     */
    public async start(): Promise<void> {
        try {
            // In the constructor, we dont have all the necessary services available; So do the init part in the start();
            this.conference = WebinarConferenceService.create(this);
            this.userContact = this.contactService.userContact;
            this.bubbleService = this.mainService.inject<BubbleServiceRB>(BUBBLE_SVC);
            this.centralizedService = this.mainService.inject("CentralizedService");
            await this.getServerWebinars();
            this.handleBubbleEvents();
            this.conference.attachAllListeners();
            const mode = 'MCU';
            this.logger.info(`[WebinarService] start (${mode}) -- success`);
            this.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
            this.started.next(true);
        }
        catch { /* ignored */ }
    }

    /**
     * This async method has to be call to stop the webinar service
     * @param nowait - when set to true the leave method is call asynchronously without waiting response of rest request,
     *  this mode has to be used when stop method is called in the onUnload browser event (try to send a sessionTerminate message
     *  when browser or tab is closed brutally)
     */
    public stop(nowait: boolean = false): void {
        if (this.conference) this.conference.detachAllListeners();
        if (this.conference.activeWebinar?.session) {
            this.conference.activeWebinar.leave(false, nowait);
        }
        if (this.subscription) { this.subscription.unsubscribe(); }
        this.webinars = [];
        this.webinarsMap = {};
        this.conference.activeWebinar = null;
        this.logger.info('[WebinarService] stop -- success');
    }

    /**
     * This asynchronous method has to be call when connection is retreive after a connection lost
     */
    public reconnect(): void {
        try {
            this.handleBubbleEvents();
            this.conference.reconnect();
        }
        catch (error) {
            this.logger.error(`[WebinarService] reconnect () -- failure -- ${stringifyError(error)}`);
        }
    }

    public waitUntilStart(): Promise<void> {
        return new Promise((resolve: any) => {
            const subscription = this.started.subscribe((started: boolean) => {
                if (started) { subscription.unsubscribe(); resolve(); }
            });
        });
    }

    /**
     *  This method permits to observe webinar change
     * @param handler - handler function called when webinar object
     * @param eventNames - (optional) event to subscribe
     */
    public subscribe(handler: any, eventNames?: string | string[]): Subscription {
        const observable = eventNames
            ? this.subject.pipe(filter((event: RBEvent) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.subject;

        return observable.subscribe((event: RBEvent) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[WebinarService] subscribe () -- error -- ${stringifyError(error)}`); }
        });
    }

    /**
     * Used to fired a webinar change event
     * @param name - name of the event
     * @param data - data free entry of the event
      */
    public submitEvent(name: string, data?: any): void { this.subject.next(RBEvent.create(name, data)); }

    public createWebinarInstance(): Webinar {
        return WebinarRB.create();
    }

    /**
     * Asynchronous method to create a new webinar on server
     * @param webinar - the webinar to create server side
     * @param addToCache - add to cache if true
     */
    public async createWebinar(webinar: Webinar, addToCache: boolean = true): Promise<Webinar> {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        let requestId = undefined;
        try {
            this.logger.info(`[WebinarService] createWebinar (correlatorId: ${correlatorId})`);

            // Create the webinar
            this.createInProgress = true;
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars`;
            const headers = this.authService.getPostHeader();
            const body = webinar.getData();
            const response = await fetch(url, { method: "POST", headers, body });
            requestId = response.headers.get("x-rainbow-request-id");
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data);
            this.updateRoles(webinar);

            // Get the associated rooms
            await this.getWebinarRooms(webinar);

            // Add to cache
            if (addToCache) this.addWebinarToCache(webinar);

            this.createInProgress = false;
            this.logger.debug(`[WebinarService] createWebinar (web: ${webinar.id}, requestId: ${requestId}, correlatorId: ${correlatorId}) -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `createWebinar (web: ${webinar.id}, requestId: ${requestId}, correlatorId: ${correlatorId})`);
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            this.createInProgress = false;
            throw errorInfo.error;
        }
    }

    public async cloneWebinar(webinar: Webinar, params: WebinarCloneParams): Promise<Webinar> {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        let requestId = undefined;
        try {
            this.logger.info(`[WebinarService] cloneWebinar (oldWebinar: ${webinar.id}, correlatorId: ${correlatorId})`);
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/clones`;
            const headers = this.authService.getPostHeader();
            const body: any = {
                webinarStartDate: params.startDate,
                webinarEndDate: params.endDate,
                speakers: params.cloneSpeakers,
                participants: params.cloneAttendees,
            };
            if (params.name) body.name = params.name;
            if (params.subject) body.subject = params.subject;
            if (params.timeZone) body.timeZone = params.timeZone;
            if (params.waitingRoomStartDate) body.waitingRoomStartDate = params.waitingRoomStartDate;
            if (params.profileId) body.profileId = params.profileId;

            const response = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });
            requestId = response.headers.get("x-rainbow-request-id");
            if (!response.ok) throw response;
            const data = (await response.json()).data;

            // Get the cloned webinar
            await wait(1000);
            const clonedWebinar = await this.getWebinar(data.id);

            // Update practice room to get associated avatar
            await this.updateWebinarPracticeRoomFromServer(clonedWebinar);

            this.logger.debug(`[WebinarService] cloneWebinar (oldWebinar: ${webinar.id}, newWebinar: ${clonedWebinar.id}, requestId: ${requestId}, correlatorId: ${correlatorId}) -- success`);
            return clonedWebinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `cloneWebinar (oldWebinar: ${webinar.id}, requestId: ${requestId}, correlatorId: ${correlatorId})`);
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to update an existing webinar on server
     * @param webinarData - the webinarData to update on server(should be partial data)
     * @param eventName - specific event name to fire (optional)
     */
    public async updateWebinar(webinarData: Webinar, eventName?: string): Promise<Webinar> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinarData.id}`;
            const headers = this.authService.getPostHeader();
            const body = webinarData.getData();
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;

            // Update webinar in cache (not the input webinar which can be partial data copy)
            const webinar = this.webinarsMap[webinarData.id];
            webinar.updateFromData(data);
            if (webinar.published) { this.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE); }
            const event = eventName ? RBEvent.create(eventName) : null;
            webinar.rxSubject.next(event);
            this.logger.info(`[WebinarService] updateWebinar -- ${webinar.id} -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "updateWebinar");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to update webinar avatar on server
     * @param webinar - the webinar to update
     * @param avatarImg - base64 encoded webinar avatar
     */
    public async updateWebinarAvatar(webinar: Webinar, avatarImg?: string): Promise<Webinar> {
        try {
            if (avatarImg) await webinar.room.setCustomAvatar(avatarImg, 1200, 600);
            else {
                await webinar.room.removeCustomAvatar();
                webinar.lastAvatarUpdate = undefined;
            }
            webinar.room.sendEvent(BubbleEvents.ON_DATA_UPDATED);
            webinar.sendEvent(WebinarEvents.ON_AVATAR_UPDATE);
            this.logger.info(`[WebinarService] updateWebinarAvatar -- ${webinar.id} -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateWebinarAvatar');
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to update webinar logo on server
     * @param webinar - the webinar to update
     * @param logoSrc - base64 encoded webinar logo
     */
    public async updateWebinarLogo(webinar: Webinar, logoSrc?: string): Promise<Webinar> {
        try {
            if (logoSrc) await webinar.practiceRoom.setCustomAvatar(logoSrc, 1200, 600);
            else {
                await webinar.practiceRoom.removeCustomAvatar();
            }
            webinar.practiceRoom.sendEvent(BubbleEvents.ON_DATA_UPDATED);
            webinar.sendEvent(WebinarEvents.ON_LOGO_UPDATE);
            this.logger.info(`[WebinarService] updateWebinarLogo -- ${webinar.id} -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "updateWebinarLogo");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to update waiting room video media for an existing webinar on server
     * @param webinarData - webinar instance which contains only "id and "waitingRoomMultimediaURL" fields
     */
    public async updateWebinarWaitingRoomMedias(webinarData: Webinar): Promise<Webinar> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinarData.id}`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ id: webinarData.id, waitingRoomMultimediaURL: webinarData.waitingRoomMultimediaURLs });
            const response = await fetch(url, { method: "PUT", headers, body });
            if (!response.ok) throw response;
            const data = (await response.json()).data;

            // Update webinar in cache (not the input webinar which can be partial data copy)
            const webinar = this.webinarsMap[webinarData.id];
            webinar.updateFromData(data);
            if (webinar.published) { this.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE); }
            this.logger.info(`[WebinarService] updateWebinarWaitingRoomMedias -- ${webinar.id} -- success`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "updateWebinarWaitingRoomMedias");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async acceptInvitation(webinarInvitation: Webinar): Promise<void> {
        try {
            let webinar = this.getWebinarById(webinarInvitation.id);
            webinar = webinar ? webinar : webinarInvitation;
            const webinarBubble = webinar.room as BubbleRB;
            if (webinarBubble.bubbleConnectedUser.status !== BubbleUserStatus.INVITED) return;
            await (webinar.room as BubbleRB).acceptInvitation();
            await this.bubbleService.updateRoomFromServer(webinar.room);
            if (!webinar.practiceRoom) { try { webinar.practiceRoom = await this.bubbleService.getBubble(webinar.roomModeratorsChatId); } catch { /**/ } }
            const conversation = await this.conversationService.getOrCreateConversation(webinar.room.jid, 'room');
            conversation.creationDate = new Date(webinar.room.creationDate);
            await this.updateWebinarFromServer(webinar);
            this.createWebinarSession(webinar, 'acceptWebinarInvitation');
            this.logger.info(`[WebinarService] acceptInvitation(web:${webinarInvitation.id}) -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "acceptInvitation");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw error;
        }
    }

    /**
     * Synchronous method to get and order webinars (no server request)
     * @param order - sort order (available values : "name", "date")
     */
    public getWebinars(order?: string): Webinar[] {
        if (order) {
            switch (order) {
                case "name":
                    this.webinars.sort((webinarA: Webinar, webinarB: Webinar) => { return webinarA.name.localeCompare(webinarB.name); });
                    break;
                default:
                    this.webinars.sort((webinarA: Webinar, webinarB: Webinar) => {
                        // moment: const dateA = moment(webinarA.webinarStartDate); const dateB = moment(webinarB.webinarStartDate);
                        // moment: if (dateA.isSame(dateB)) return 0; if (dateA.isBefore(dateB)) return -1; return 1;
                        const dateA = new Date(webinarA.webinarStartDate).getTime();
                        const dateB = new Date(webinarB.webinarStartDate).getTime();
                        if (dateA === dateB) return 0;
                        if (dateA < dateB) return -1;
                        return 1;
                    });
                    break;
            }
        }
        return this.webinars.filter((webinar: Webinar) => {
            return webinar.isAttendee || webinar.isTerminated() || webinar.room.bubbleConnectedUser.status === BubbleUserStatus.ACCEPTED;
        });
    }

    /**
     * Asynchronous method to retreive all webinars from server
     */
    private async getServerWebinars(): Promise<Webinar[]> {
        try {
            this.logger.info(`[WebinarService] getServerWebinars ()`);
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars?limit=100&offset=0`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            const data = (await response.json()).data;

            // Get active and futur webinar rooms
            await this.getActiveAndFutureWebinarsRooms(data);

            this.webinars = data.reduce((webinars: Webinar[], webinarData: any) => {

                if (!webinarData.moderatorsSelectedAnNotified) {
                    this.deleteWebinar(webinarData.id);
                    this.logger.error(`[WebinarService] getServerWebinars () -- remove non finalized webinar -- ${webinarData.id}`);
                    return webinars;
                }

                let webinar = this.webinarsMap[webinarData.id];
                if (webinar) webinar.updateFromData(webinarData);
                else webinar = WebinarRB.createFromData(webinarData);

                this.updateRoles(webinar);

                if (!webinar.isTerminated()) {
                    webinar.room = this.bubbleService.getBubbleFromCache(webinar.roomId);
                    webinar.practiceRoom = this.bubbleService.getBubbleFromCache(webinar.roomModeratorsChatId);

                    if (webinar.room) { this.createWebinarSession(webinar, 'getServerWebinars'); }
                    if (!webinar.isAttendee && !webinar.room) {
                        if (webinar.isCreator) {
                            //this.deleteWebinar(webinarData.id);
                            this.logger.error(`[WebinarService] getServerWebinars () -- no room found for webinar ${webinarData.id} roomId ${webinar.roomId} -- remove corrupted webinar`);
                        }
                        else this.logger.warn(`[WebinarService] getServerWebinars () -- no room found for webinar ${webinarData.id} roomId ${webinar.roomId})`);
                        return webinars;
                    }
                }
                // Ignore non published webinar for attendee
                if (webinar.isAttendee && !webinar.published) return webinars;

                // Store in cache only non terminated webinars
                if (!this.webinarsMap[webinar.id]) {
                    this.webinarsMap[webinar.id] = webinar;
                    webinars.push(webinar);
                    this.logger.debug(`[WebinarServiceDebug] getServerWebinars () push webinar ${webinar.id} in cache`);
                }

                return webinars;
            }, []);
            this.logger.info(`[WebinarService] getServerWebinars () -- ${this.webinars.length} webinar(s) found -- success`);
            return this.webinars;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getServerWebinars");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            return [];
        }
    }

    /**
     * Asynchronous method to retreive webinar room from server
     * @param webinarsData - data
     */
    public async getActiveAndFutureWebinarsRooms(webinarsData: any): Promise<void> {

        const roomsIdentifiers = webinarsData.reduce((roomsIds: string[], webinarData: any) => {
            if (webinarData.state !== 'terminated') {
                roomsIds.push(webinarData.roomId);
                roomsIds.push(webinarData.roomModeratorsChatId);
                this.logger.info(`[WebinarService] getActiveAndFutureWebinarsRooms (web: ${webinarData.id}, roomId: ${webinarData.roomId}, practId: ${webinarData.roomModeratorsChatId})`);
            }
            return roomsIds;
        }, []);

        try { await this.bubbleService.getServerBubblesByIds(roomsIdentifiers, false, true); } catch {
            this.logger.error(`[WebinarService] getActiveAndFutureWebinarsRooms -- failed -- getServerRooms`);
        }
    }

    /**
     * Asynchronous method to retreive webinar from server (reentrant ready method)
     * @param webinarId - the identifiant of webinar to get
     */
    public getWebinar(webinarId: string): Promise<Webinar> {

        if (this.webinarsMap[webinarId]) {
            this.logger.debug(`[WebinarService] getWebinar (web: ${webinarId}, from: cache) -- success`);
            return Promise.resolve(this.webinarsMap[webinarId]);
        }

        // solution: if (!(webinarId in this.getWebinarPromise)) {
        // need tests to be tested though
        if (!this.getWebinarPromise[webinarId]) {
            this.getWebinarPromise[webinarId] = new Promise<Webinar>((resolve, reject) => {

                const webinar = WebinarRB.create();
                webinar.id = webinarId;
                this.updateWebinarFromServer(webinar)
                    .then(() => {
                        this.addWebinarToCache(webinar);

                        this.logger.debug(`[WebinarService] getWebinar (web: ${webinarId}, from: server) -- success`);
                        delete this.getWebinarPromise[webinarId];
                        this.updateRoles(webinar);

                        // Handle existing janus sessions (only one)
                        this.createWebinarSession(webinar, 'getWebinar');

                        resolve(webinar);
                    })
                    .catch((error: unknown) => {
                        this.logger.error(`[WebinarService] getWebinar (web: ${webinarId}) -- ${stringifyError(error)}`);
                        delete this.getWebinarPromise[webinarId];
                        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                        reject(error);
                    });

            });
        }
        return this.getWebinarPromise[webinarId];
    }

    /**
     * Synchronous method to retreive webinar from cache
     * @param webinarId - the identifiant of webinar to get
     */
    public getWebinarById(webinarId: string): Webinar | undefined {
        return this.webinarsMap[webinarId];
    }

    /**
     * Method to retreive webinar from cache
     * @param sessionId - session identifier
     * @param confId - the conference identifier (optional)
     */
    public getWebinarBySessionId(sessionId: string, confId?: string): Webinar | undefined {
        let conferenceId = confId;
        if (!conferenceId) {
            const session = this.xmppService.connection.jingle.sessions[sessionId];
            if (!session) return undefined;
            conferenceId = session.conference_id;
        }
        return this.webinars.find((webinar: Webinar) => { return webinar.roomId === conferenceId; });
    }

    /**
     * Method to retreive webinar from cache
     * @param roomId - room identifier
     */
    public getWebinarByRoomId(roomId: string): Webinar | undefined {
        return this.webinars.find((webinar: Webinar) => { return webinar.roomId === roomId; });
    }

    /**
     * Asynchronous method to retreive public webinar informations (this method can be call without authentication)
     * @param openInviteId - the webinar openInviteId
     * @param joinUuid - the webinar joinUuid
     * @param registrationCall - the registrationClickCount for statisitics (optional)
     */
    public async getPublicWebinarInfo(openInviteId: string, joinUuid: string, registrationCall?: boolean): Promise<any> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/public`;
            if (registrationCall) url += '?registrationClickCount=true';
            const headers = { 'Accept': 'application/json', 'Content-Type': 'application/json' };
            const bodyContent: any = {};
            if (!joinUuid) { bodyContent.registrationId = openInviteId; }
            if (joinUuid) {
                bodyContent.openInviteId = openInviteId;
                bodyContent.joinUuid = joinUuid;
            }
            const body = JSON.stringify(bodyContent);
            const response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) throw response;
            const webinarInfo = (await response.json()).data;
            webinarInfo.errorDetailsCode = null;
            webinarInfo.publicWebinarId = openInviteId;
            webinarInfo.joinUuid = joinUuid;
            this.logger.info(`[WebinarService] -- getPublicWebinarInfo -- success`);
            return webinarInfo;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getWebinar');
            this.logger.error(`[WebinarService] -- getPublicWebinarInfo -- failure -- ${errorInfo.message}`);
            if (errorInfo.error.errorDetailsCode === 403621) {
                const webinarInfo = errorInfo.error.errorDetailsData;
                webinarInfo.errorDetailsCode = 403621;
                return webinarInfo;
            }
            return { errorStatus: errorInfo.error.status, errorDetailsCode: errorInfo.error.errorDetailsCode, errorMessage: errorInfo.error.message, linkType: '' };
        }
    }

    /**
     * Asynchronous method to update the webinar already in cache with server data
     * @param webinar - the webinar openInviteId
     */
    public async updateWebinarFromServer(webinar: Webinar): Promise<Webinar> {
        const correlatorId = Math.random().toString(36).slice(2, 12);
        let requestId = undefined;
        try {
            this.logger.info(`[WebinarService] updateWebinarFromServer (web: ${webinar.id}, correlatorId: ${correlatorId})`);

            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            requestId = response.headers.get("x-rainbow-request-id");
            if (!response.ok) throw response;
            const data = (await response.json()).data;

            webinar.updateFromData(data);
            this.updateRoles(webinar);

            if (!webinar.isTerminated()) {
                if (!webinar.room) { try { webinar.room = await this.bubbleService.getBubble(webinar.roomId) as BubbleRB; } catch { /**/ } }

                // Only try to get practice room if the user is accepted cause user is not added on practice room until he is accepted in main room
                if (!webinar.practiceRoom && (webinar.isOrganizer || webinar.isSpeaker) && webinar.room && webinar.room.bubbleConnectedUser.status === BubbleUserStatus.ACCEPTED) {
                    if (!webinar.practiceRoom) { try { webinar.practiceRoom = await this.bubbleService.getBubble(webinar.roomModeratorsChatId) as BubbleRB; } catch { /**/ } }
                }

                if (!webinar.session) this.createWebinarSession(webinar, 'updateWebinarFromServer');
            }

            this.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
            this.logger.debug(`[WebinarService] updateWebinarFromServer (web: ${webinar.id}, correlatorId: ${correlatorId}, requestId: ${requestId})`);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, `updateWebinarFromServer (web: ${webinar.id}, correlatorId: ${correlatorId}, requestId: ${requestId})`);
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to delete a webinar
     * @param webinarId - the webinarId
     */
    public async deleteWebinar(webinarId: string): Promise<void> {
        try {
            const webinar: Webinar = this.webinarsMap[webinarId];
            if (webinar && !webinar.isOrganizer && !webinar.isCreator) {
                const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinarId}/unsubscribe`;
                const headers = this.authService.getPostHeader();
                const response = await fetch(url, { method: "PUT", headers });
                if (!response.ok) throw response;
            }
            else {
                const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinarId}`;
                const headers = this.authService.getRequestHeader();
                const response = await fetch(url, { method: "DELETE", headers });
                if (!response.ok) throw response;
            }
            if (webinar) this.removeWebinar(webinar);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "deleteWebinar");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method to update webinar role fields
     * @param webinar - the webinar to update
     */
    public updateRoles(webinar: Webinar): void {
        const userContactId = this.contactService.userContact.dbId;
        webinar.isOrganizer = webinar.organizers.find((organizer: any) => { return (organizer.userId === userContactId) || (organizer.alternativeUserId && organizer.alternativeUserId === userContactId); }) !== undefined;
        webinar.isSpeaker = webinar.speakers.find((speaker: any) => { return (speaker.userId && speaker.userId === userContactId) || (speaker.alternativeUserId && speaker.alternativeUserId === userContactId); }) !== undefined;
        webinar.isAttendee = !webinar.isOrganizer && !webinar.isSpeaker;
        webinar.isGuestAttendee = webinar.isAttendee && this.contactService.userContact.isGuest();
        webinar.isGuest = this.contactService.userContact.isGuest();
        webinar.isCreator = webinar.creatorId === userContactId;
    }

    public async updateWebinarRoomFromServer(webinar: Webinar): Promise<Webinar> {
        this.logger.info(`[WebinarService] updateWebinarRoomFromServer -- ${webinar.id}`);
        await this.bubbleService.updateRoomFromServer(webinar.room as BubbleRB);
        return webinar;
    }

    public async updateWebinarPracticeRoomFromServer(webinar: Webinar): Promise<Webinar> {
        this.logger.info(`[WebinarService] updateWebinarPracticeRoomFromServer -- ${webinar.id}`);
        await this.bubbleService.updateRoomFromServer(webinar.practiceRoom as BubbleRB);
        return webinar;
    }

    /**
     * Asynchronous method to check if a user has a webinar licence
     * @param userId - the webinarId
     */
    public async hasWebinarLicence(userId: string): Promise<boolean> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/invitations/check-license`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ users: [userId] });
            const response = await fetch(url, { method: "POST", headers, body });
            if (!response.ok) throw response;
            const data = await response.json();
            return data.status.accepted.users[0] === userId;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getSpeakerStatus");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to get available licence between a starting and ending dates
     * @param webinarStartDate - the start date
     * @param webinarEndDate - the end datae
     * @param excludeWebinarId - the webinarId to exclude frow search (optional)
     */
    public async getAvailableLicences(webinarStartDate: string, webinarEndDate: string, excludeWebinarId?: string): Promise<any> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/licenses?webinarStartDate=${webinarStartDate}&webinarEndDate=${webinarEndDate}`;
            if (excludeWebinarId) { url += `&excludeWebinarId=${excludeWebinarId}`; }
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            const unorderedLicences = (await response.json()).data;
            const orderedLicences = unorderedLicences.sort((lic1: any, lic2: any) => {
                if (lic1.maxParticipants > lic2.maxParticipants) return 1;
                if (lic1.maxParticipants < lic2.maxParticipants) return -1;
                return 0;
            });
            return orderedLicences;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getAvailableLicences");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to notified webinar moderators
     * @param webinar - the webinar
     */
    public async notifyModerators(webinar: Webinar): Promise<Webinar> {
        if (webinar.moderatorsNotified) { return webinar; }
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/warn-moderators`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "PUT", headers });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data);
            this.submitEvent(WebinarServiceEvents.WEBINAR_PUBLISHED, webinar);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "warnModerators");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to publish a webinar
     * @param webinar - the webinar
     */
    public async publishWebinar(webinar: Webinar): Promise<Webinar> {
        if (webinar.published) { return webinar; }
        try {
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/publish`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "PUT", headers });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            webinar.updateFromData(data);
            webinar.rxSubject.next(null);
            return webinar;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "publishWebinar");
            this.logger.error(`[WebinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Compute the webinar color depending its name
     * @param name - the webinar name
     */
    public computeWebinarColor(name: string): string {
        const upperCaseWebinarName = name.toUpperCase();
        const colors = ["#d3a575", "#eb8d8d", "#d47ab0", "#b38deb", "#9aa3ed", "#76a6e5", "#92d0de", "#a7d6bc", "#99c86b", "#b4d43d", "#dcd33b", "#eec838"];
        let sum = 0;
        for (let i = 0; i < upperCaseWebinarName.length; i++) { sum += upperCaseWebinarName.charCodeAt(i); }
        return colors[sum % 12];
    }

    /**
     * Subscribe to room events (observable and xmpp events)
     */
    private handleBubbleEvents(): void {
        if (this.subscription) { this.subscription.unsubscribe(); }
        this.subscription = this.bubbleService.subscribe(async (event: RBEvent) => {
            try {
                switch (event.name) {
                    case BubbleServiceEvents.ON_USER_INVITATION_ACCEPTED:
                    case BubbleServiceEvents.ON_USER_INVITATION_DECLINED:
                    case BubbleServiceEvents.ON_USER_BUBBLE_REMOVED:
                    case BubbleServiceEvents.ON_USER_BUBBLE_LEFT: {
                        // Determines whether the event concerns the connected user or another member of the bubble
                        const bubble = event.data?.bubble;
                        const bubbleUser: BubbleUser = event.data?.bubbleUser;

                        if (bubbleUser.user.isConnectedUser()) {
                            // Event related to the connected user
                            if (bubble?.isWebinarBubble() && (bubble.bubbleConnectedUser.status === BubbleUserStatus.UNSUBSCRIBED || bubble.bubbleConnectedUser.status === BubbleUserStatus.DELETED)) {
                                const webinar = this.webinarsMap[bubble.conference.webinar.id];
                                if (webinar) {
                                    this.logger.infoEvent(`[webinarService] onWebinarRemove (web: ${webinar.id})`);
                                    this.removeWebinar(webinar);
                                }
                            }
                        } else {
                            // Event related to the any other member of the bubble
                            if (bubble?.isWebinarBubble()) {
                                const webinar = this.webinarsMap[bubble.conference.webinar.id];
                                if (webinar) {
                                    this.logger.infoEvent(`[webinarService] onWebinarUpdate (web: ${webinar.id})`);
                                    await this.updateWebinarFromServer(webinar);
                                    // @ts-expect-error - since we accept `any`, passing nothing should also we allowed (TODO: fix this line)
                                    webinar.rxSubject.next();
                                }
                            }
                        }
                    } break;

                    default: break;
                }
            }

            catch { /* */ }
        });

        this.xmppHandlers.forEach((handler: any) => { this.xmppService.connection.deleteHandler(handler); });
        this.xmppHandlers = [];
        this.xmppHandlers.push(this.xmppService.connection.addHandler((stanza: any) => { return this.onManagementMessage(stanza); }, null, "message", "management"));
    }

    /**
     * Xmpp room event handler
     * @param stanza - the xmpp stanza event
     */
    private async onManagementMessage(stanza: any) {
        try {
            const stanzaElem = $(stanza);
            const webinarElem = stanzaElem.find("webinar");

            let webinar: Webinar;
            if (webinarElem.length > 0) {
                const webinarId = webinarElem.attr("webinarid");
                webinar = this.webinarsMap[webinarId];

                // Handle action messages
                const action = webinarElem.attr("action");
                if (action) {
                    switch (action) {
                        case "create":
                            if (!webinar && !this.createInProgress) {
                                webinar = await this.getWebinar(webinarId);
                                this.logger.infoEvent(`[webinarService] onManagementMessage (web: ${webinarId}) -- create`);
                            }
                            break;
                        case "update":
                            if (webinar) {
                                // Handle webinar info message
                                const nameElem = webinarElem.find("name");
                                const subjectElem = webinarElem.find("subject");
                                const approvalElem = webinarElem.find("approvalRegistrationMethod");
                                const moderatorsNotifiedElem = webinarElem.find("moderatorsSelectedAnNotified");
                                const emailNotificationElem = webinarElem.find("emailNotification");
                                const chatElem = webinarElem.find("chat");
                                const waitingRoomStartDateElem = webinarElem.find("waitingRoomStartDate");
                                const webinarStartDateElem = webinarElem.find("webinarStartDate");
                                const webinarEndDateElem = webinarElem.find("webinarEndDate");
                                const timeZoneElem = webinarElem.find("timeZone");
                                const waitingRoomMultimediaURLElem = webinarElem.find("waitingRoomMultimediaURL");
                                const roomLastAvatarUpdateDateElem = webinarElem.find("roomLastAvatarUpdateDate");
                                const recording = webinarElem.find("recording");

                                // Hack to avoid to erase waitingRoomMultimediaURLs when adding a participant... (no waitingRoomMultimediaURLs in event)
                                if (webinarElem.find("participants").length === 0) webinar.waitingRoomMultimediaURLs = [];

                                if (waitingRoomMultimediaURLElem.length) {
                                    const urlsElem = waitingRoomMultimediaURLElem.find('url');
                                    urlsElem.each((__ind: number, urlElem: any) => { webinar.waitingRoomMultimediaURLs.push($(urlElem).text()); });
                                }
                                if (chatElem.length && chatElem.attr('option')) {
                                    if (webinar.chatOption !== chatElem.attr('option')) {
                                        webinar.chatOption = chatElem.attr('option');
                                        webinar.sendEvent(WebinarEvents.ON_CHAT_OPTIONS_UPDATE);
                                    }
                                }

                                if (roomLastAvatarUpdateDateElem.length) {
                                    webinar.lastAvatarUpdate = roomLastAvatarUpdateDateElem.text();
                                    webinar.sendEvent(WebinarEvents.ON_AVATAR_UPDATE);
                                }

                                if (nameElem.length) webinar.name = nameElem.text();
                                if (subjectElem.length) webinar.subject = subjectElem.text();
                                if (approvalElem.length) webinar.approvalRegistrationMethod = approvalElem.text();
                                if (moderatorsNotifiedElem.length) webinar.moderatorsNotified = moderatorsNotifiedElem.text() === 'true';
                                if (emailNotificationElem.length) webinar.published = emailNotificationElem.text() === 'true';
                                if (waitingRoomStartDateElem.length) webinar.waitingRoomStartDate = waitingRoomStartDateElem.text();
                                if (webinarStartDateElem.length) webinar.webinarStartDate = webinarStartDateElem.text();
                                if (webinarEndDateElem.length) webinar.webinarEndDate = webinarEndDateElem.text();
                                if (timeZoneElem.length) webinar.timeZone = timeZoneElem.text();
                                if (recording.length) webinar.recorded = recording.text() === 'true' ? 'true' : 'false';

                                // Handle moderators messages
                                const moderatorsElem = webinarElem.find("moderators");
                                if (moderatorsElem.length > 0) {
                                    webinar.organizers = [];
                                    const organizersElem = moderatorsElem.find("organizer");
                                    organizersElem.each((__ind: number, organizer: any) => {
                                        const organizerElem = $(organizer);
                                        webinar.organizers.push({
                                            userId: organizerElem.attr("userid"),
                                            status: organizerElem.attr("status"),
                                            hightlighted: organizerElem.attr("hightlighted") === 'true',
                                            role: "organizer"
                                        });
                                    });

                                    webinar.speakers = webinar.speakers.filter((speaker: any) => { return speaker.userId === null; });
                                    const speakersElem = moderatorsElem.find("speaker");
                                    speakersElem.each((__ind: number, speaker: any) => {
                                        const speakerElem = $(speaker);
                                        const userId = speakerElem.attr("userid");
                                        webinar.speakers.push({
                                            userId,
                                            status: speakerElem.attr("status"),
                                            company: speakerElem.attr("company"),
                                            jobTitle: speakerElem.attr("jobTitle"),
                                            hightlighted: speakerElem.attr("hightlighted") === 'true',
                                            role: "speaker"
                                        });
                                        // If contact not in cache, load it asynchronously
                                        this.contactService.getContactByDBId(userId);
                                    });

                                    const guestspeakersElem = moderatorsElem.find("guestspeaker");
                                    guestspeakersElem.each((__ind: number, guestspeaker: any) => {
                                        const guestspeakerElem = $(guestspeaker);
                                        webinar.speakers.push({
                                            email: guestspeakerElem.attr("email"),
                                            status: guestspeakerElem.attr("status"),
                                            role: "speaker"
                                        });
                                    });

                                    this.updateRoles(webinar);
                                }

                                // Handle participants messages
                                const participantsElem = webinarElem.find("participants");
                                if (participantsElem.length > 0) {
                                    this.logger.infoEvent(`[webinarService] onManagementMessage (web: ${webinarId}) -- update ${participantsElem.length.toString()} participant(s)`);

                                    participantsElem.find("participant").each((__ind: number, _participant: any) => {

                                        // Find existing participant
                                        const participantElem = $(_participant);
                                        const email: string = participantElem.attr('email');
                                        const state: string = participantElem.attr('state');
                                        let participant = webinar.participants.find((part: any) => { return part.email === email; });

                                        if (participant) { participant.state = state; }
                                        else {
                                            participant = WebinarParticipant.create();
                                            participant.email = email;
                                            participant.state = state;
                                            participant.firstName = participantElem.attr('firstName');
                                            participant.lastName = participantElem.attr('lastName');
                                            participant.company = participantElem.attr('company');
                                            participant.jobTitle = participantElem.attr('jobTitle');
                                            participant.userId = participantElem.attr('userId');
                                            participant.lastAvatarUpdateDate = participantElem.attr('lastAvatarUpdateDate');
                                            webinar.participants.push(participant);
                                        }
                                    });
                                    webinar.updateAttendeeCounters();
                                    /*webinar.pendingParticipantNumber = parseInt(participantsElem.attr('pending'), 10);
                                    webinar.acceptedParticipantNumber = parseInt(participantsElem.attr('accepted'), 10);
                                    webinar.rejectedParticipantNumber = parseInt(participantsElem.attr('rejected'), 10);*/
                                }
                                webinar.rxSubject.next(null);
                                this.logger.infoEvent(`[webinarService] onManagementMessage (web: ${webinarId}) -- update`);
                            }
                            break;
                        case "delete":
                            if (webinar) {
                                this.logger.infoEvent(`[webinarService] onManagementMessage (web: ${webinarId}) -- delete`);
                                if (webinar.room && webinar.room.bubbleConnectedUser.status === BubbleUserStatus.INVITED) {
                                    webinar.room.bubbleConnectedUser.status = BubbleUserStatus.UNSUBSCRIBED;
                                    this.bubbleService.sendEvent(BubbleServiceEvents.ON_USER_BUBBLE_LEFT, { bubble: webinar.room, bubbleUser: webinar.room.bubbleConnectedUser });
                                }
                                this.removeWebinar(webinar);
                            }
                            break;
                        case "invitation": {
                            if (!webinar) webinar = await this.getWebinar(webinarId);
                            const from = stanzaElem.attr('from');
                            if (webinar?.isAttendee) { webinar.published = true; }
                            this.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, from, action: 'invite' });
                            this.logger.infoEvent(`[webinarService] onManagementMessage (web: ${webinarId}) -- invitation`);
                        } break;
                        case "invitation-cancelled":
                            if (!webinar) webinar = await this.getWebinar(webinarId);
                            await webinar.leave(false, false, 'Invitation cancelled');
                            this.removeWebinar(webinar);
                            this.logger.infoEvent(`[webinarService] onManagementMessage (web: ${webinarId}) -- invitation-cancelled"`);
                            break;
                        default:
                            break;
                    }
                    this.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
                }
            }

            const webinarInvitationElems = stanzaElem.find("invitation");
            if (webinarInvitationElems.length > 0) {
                if (webinarInvitationElems.attr("deny") === 'true') {
                    const speakerFirstName = webinarInvitationElems.attr("firstname");
                    const speakerLastName = webinarInvitationElems.attr("lastname");
                    let from: string;
                    if (speakerFirstName && speakerLastName) from = `${speakerLastName} ${speakerFirstName}`;
                    else if (speakerFirstName) from = speakerFirstName;
                    else if (speakerLastName) from = speakerLastName;
                    else from = webinarInvitationElems.attr("email");
                    this.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { action: 'speakerInvitationRefused', webinar, from });
                }
            }

            this.handleWebinarRecording(stanza);
            return true;
        }
        catch (error) {
            this.logger.error(`[webinarService] onWebinarManagement () -- failure --  ${stringifyError(error)}`);
            return true;
        }
    }

    private handleWebinarRecording(stanza: any) {
        try {
            const stanzaElem = xmlParser(stanza);
            const from = stanzaElem.attr('from');
            const webinarRecordingElem = stanzaElem.find('webinarrecordingfile');
            if (webinarRecordingElem.length > 0) {
                const action = webinarRecordingElem.attr('action');
                if (action === 'create') {
                    const webinarId = webinarRecordingElem.find('webinarid').text();
                    const webinar: Webinar = this.webinarsMap[webinarId];
                    if (webinar) {
                        webinar.recordFileId = webinarRecordingElem.find('fileid').text();
                        webinar.recordStatus = webinarRecordingElem.find('status').text();
                        webinar.rxSubject.next(null);
                        if (webinar.recordStatus === 'release_complete') this.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, from, action: 'recordComplete' });
                        if (webinar.recordStatus === 'failed') this.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, from, action: 'recordFailure' });
                        this.logger.info("[webinarService] onWebinarManagement - handleWebinarRecording -- create -- " + webinar.recordFileId);
                    }
                }
                else if (action === 'abort') {
                    const webinarId = webinarRecordingElem.find('webinarid').text();
                    const webinar: Webinar = this.webinarsMap[webinarId];
                    if (webinar) {
                        webinar.recordStatus = 'abort';
                        webinar.rxSubject.next(null);
                        this.submitEvent(WebinarServiceEvents.WEBINAR_NOTIFICATION, { webinar, from, action: 'recordAbort' });
                    }
                }
            }
        }
        catch (error) {
            this.logger.error("[webinarService] onWebinarManagement - handleWebinarRecording -- failure -- " + error.message);
        }
    }

    /**
     * Asynchronous method to get webinar associated room from room service
     * @param webinar - the webinar to upadte
     * @param practice - when true also get practice room
     */
    public async getWebinarRooms(webinar: Webinar, practice: boolean = true): Promise<void> {
        webinar.room = await this.bubbleService.getServerBubble(webinar.roomId);
        if (practice) { webinar.practiceRoom = await this.bubbleService.getServerBubble(webinar.roomModeratorsChatId); }
    }

    /**
     * Method to check if connected user has active or futur webinars
     */
    public hasActiveOrFutureWebinar(): boolean {
        return this.webinars.some((webinar: Webinar) => { return webinar.isOrganizer || webinar.status !== WebinarStatus.TERMINATED; });
    }

    public hasWebinar(): boolean {
        return this.webinars.length > 0;
    }

    /**
     * Internal method to remove a webinar from the cache
     * @param webinar - the webinar to remove from cache
     */
    public removeWebinar(webinar: Webinar): void {
        const index = this.webinars.findIndex((web: Webinar) => { return web.id === webinar.id; });
        if (index !== -1) { this.webinars.splice(index, 1); delete this.webinarsMap[webinar.id]; }
        this.conference.removeActiveWebinar(webinar);
        webinar.session = null;
        webinar.status = WebinarStatus.TERMINATED;
        webinar.rxSubject.next(null);
        this.submitEvent(WebinarServiceEvents.WEBINARS_UPDATE);
    }

    /**
     * getWebinarConversation
     * @param webinar - the webinar
     */
    public async getWebinarTextConversation(webinar: Webinar): Promise<string> {
        try {
            const conversation = await this.conversationService.getOrCreateConversation(webinar.roomId, 'room');
            const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/webinars/${webinar.id}/conversations/${conversation.dbId}/downloads`;
            const response = await fetch(url, { method: 'GET', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[webinarService] getWebinarTextConversation -- ${webinar.id} -- success`);
            const data = (await response.json()).data;
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getWebinarTextConversation');
            this.logger.error(`[webinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getGlobalAnalytics
     * @param webinar - the webinar
     */
    public async getGlobalAnalytics(webinar: Webinar): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/analytics/v1.0/webinars/${webinar.id}`;
            const response = await fetch(url, { method: 'GET', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[webinarService] getAnalytics -- ${webinar.id} -- success`);
            const data = (await response.json()).data;
            data.participation = (data.registered > 0) ? `${Math.floor(data.participants * 100 / data.registered).toString()} %` : `0 %`;
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAnalytics');
            this.logger.error(`[webinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getParticipantsAnalytics
     * @param webinar - the webinar
     */
    public async getParticipantsAnalytics(webinar: Webinar): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/analytics/v1.0/webinars/${webinar.id}/participants`;
            const response = await fetch(url, { method: 'GET', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[webinarService] getParticipantsAnalytics -- ${webinar.id} -- success`);
            const data = (await response.json()).data;
            // moment: data.forEach((entry: any) => { entry.date = moment(entry.date).format('LT'); });
            data.forEach((entry: any) => { entry.date = format(new Date(entry.date), "p"); });
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getParticipantsAnalytics');
            this.logger.error(`[webinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * getAttendeesAnalytics
     * @param webinar - the webinar
     */
    public async getAttendeesAnalytics(webinar: Webinar): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/analytics/v1.0/webinars/${webinar.id}/attendance`;
            const response = await fetch(url, { method: 'GET', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[webinarService] getAttendeesAnalytics -- ${webinar.id} -- success`);
            const data = (await response.json()).data;
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAttendeesAnalytics');
            this.logger.error(`[webinarService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * add a webinar to service cache
     * @param webinar - the webinar
     */
    public addWebinarToCache(webinar: Webinar): void {
        if (this.webinarsMap[webinar.id]) {
            this.logger.warn(`[webinarService] addWebinarToCache (web: ${webinar.id}) -- already in cache -- ignore`);
            return;
        }
        this.webinars.push(webinar);
        this.webinarsMap[webinar.id] = webinar;
    }

    /**
     * Create webinar session from bubble information
     * @param webinar - the webinar
     * @param from - the origin of the request
     */
    public createWebinarSession(webinar: Webinar, from: string): void {
        const localParticipantId = this.contactService.userContact.dbId;
        const bubble = webinar.room as BubbleRB;
        if (bubble?.conference?.sessions.length) {
            const message: string = `[webinarService] createWebinarSession (web: ${webinar.id}, part: ${localParticipantId}, type: ${bubble.conference.sessions[0].type}, from: ${from})`;
            switch (bubble.conference.sessions[0].type) {
                case 'practiceWaitingRoom': webinar.session = WebinarSession.create(); this.logger.debug(message); break;
                case 'practiceRoom': if (!webinar.isAttendee) { webinar.session = WebinarSession.create(); this.logger.debug(message); } break;
                case 'waitingRoom': if (webinar.isAttendee) { webinar.session = WebinarSession.create(); this.logger.debug(message); } break;
                case 'webinar': webinar.session = WebinarSession.create(); this.logger.debug(message); break;
                default: break;
            }
        }
        else this.logger.debug(`[webinarService] createWebinarSession (web: ${webinar.id}, part: ${localParticipantId}, from: ${from}) -- no room conference session`);
    }

    /**
     * Get all webinars scheduled for a given time slot
     * @param startDate - the begin of the time slot
     * @param endDate - the end of the time slot
     */
    public async getScheduledWebinars(startDate: string, endDate: string): Promise<any[]> {
        const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/schedule?startDate=${startDate}&endDate=${endDate}`;
        const headers = this.authService.getRequestHeader();
        const response = await fetch(url, { method: "GET", headers: headers });
        if (!response.ok) {
            throw (response);
        }
        const jsonResponse: any = (await response.json()).data;
        return jsonResponse;
    }

    /**
     * Get all licences available of the user's company
     */
    public async getAllLicences(): Promise<any[]> {
        const url = `${config.restServerUrl}/api/rainbow/webinar/v1.0/all-licenses`;
        const headers = this.authService.getRequestHeader();
        const response = await fetch(url, { method: "GET", headers: headers });
        if (!response.ok) throw (response);
        const allLicences: any = (await response.json()).data;
        return allLicences;
    }

    /**
     * Compute busy slot
     */
    public async computeBusySlots(date: string): Promise<void> {
        const slots = Array.from({ length: 24 }, (v, i) => ({ begin: (i < 10 ? '0' : '') + i + ":00", end: "", duration: 0, height: null, top: null, state: "notBusy", webinarsIn: [] }));
        const busySlots = Array.from({ length: 24 }, (v, i) => ({ height: null, top: null, state: "notBusy" }));

        const allLicences = await this.getAllLicences();
        const numberLicences = allLicences.reduce((nbLicences: number, licence: any) => { nbLicences += licence.totalLicenses; return nbLicences; }, 0);

        this.webinars.forEach((webinar: Webinar) => {
            const startDate = format(new Date(webinar.webinarStartDate), 'yyyy-MM-dd');
            const endDate = format(new Date(webinar.webinarEndDate), 'yyyy-MM-dd');
            let end = this.getHours(webinar.webinarEndDate);
            const endString = format(new Date(webinar.webinarEndDate), 'HH:mm');
            const beginString = format(new Date(webinar.webinarStartDate), 'HH:mm');
            const beginMinutes = this.getMinutes(webinar.webinarStartDate);
            if (startDate !== date && (endDate !== date || (endDate === date && end === 0)))
                return;
            if (startDate === date && endDate !== date)
                webinar.webinarEndDate = new Date(startDate + " 23:59").toISOString();
            if (startDate !== date && endDate === date)
                webinar.webinarStartDate = new Date(endDate + " 00:00").toISOString();

            const begin = this.getHours(webinar.webinarStartDate);
            end = this.getHours(webinar.webinarEndDate);
            const endMinutes = this.getMinutes(webinar.webinarEndDate);
            if (end === 0)
                end = 24;
            let idx = 0;
            for (idx = begin; idx <= end; idx++) {
                let durationSlot = differenceInMinutes(new Date(webinar.webinarEndDate), new Date(webinar.webinarStartDate));
                let height = (durationSlot * 60) / 60;
                let top = (beginMinutes * 60) / 60;
                if (idx === end && endMinutes === 0)
                    continue;
                if (slots[idx].webinarsIn.length < numberLicences)
                    slots[idx].webinarsIn.push({ begin: beginString, end: endString, name: webinar.name, id: webinar.id });
                const intersection = this.findIntersection(slots[idx].webinarsIn);
                if (slots[idx].webinarsIn.length >= numberLicences && intersection !== null) {

                    durationSlot = this.deltaInMinutes(intersection.begin, intersection.end);
                    let busyHeight = (durationSlot * 60) / 60;
                    const index = this.myParseInt(intersection.begin);

                    let busyTop = (parseInt(intersection.begin.substring(3, 5), 10) * 60) / 60;
                    if (busySlots[index].top === null) busySlots[index].top = busyTop;
                    if (busySlots[index].height === null) busySlots[index].height = busyHeight;

                    let difference;
                    if (busySlots[index].top < busyTop) {
                        difference = busyTop - busySlots[index].top;
                        busyTop = busySlots[index].top;
                    }
                    else difference = busySlots[index].top - busyTop;

                    busyHeight = (busyHeight + difference > busySlots[index].height ? busyHeight + difference : busySlots[index].height);
                    busySlots[index].height = busyHeight;
                    busySlots[index].top = busyTop;
                    busySlots[index].state = "busy";
                }

                if (slots[begin].top === null) slots[begin].top = top;
                if (slots[begin].height === null) slots[begin].height = height;
                if (slots[begin].top < top) {
                    const difference = top - slots[begin].top;
                    height = (height + difference > slots[begin].height ? height + difference : slots[begin].height);
                    top = slots[begin].top;
                }
                else {
                    const difference = slots[begin].top - top;
                    height = (slots[begin].height + difference > height ? slots[begin].height + difference : height);
                }
                slots[begin] = { begin: slots[begin].begin, end: endString, duration: durationSlot, height: height, top: top, state: "partiallyBusy", webinarsIn: slots[begin].webinarsIn };
            }
        });
    }

    public async downloadRecord(webinar: Webinar): Promise<void> {
        const fileDescriptor = await this.fileStorageService.retrieveAndStoreOneFileDescriptor(webinar.recordFileId);
        await this.centralizedService?.fileSharing.downloadFile(fileDescriptor);
        this.fileStorageService.removeFileDescriptorById(fileDescriptor.id);
    }

    private getHours(date: string): number {
        return (new Date(date).getHours());
    }

    private getMinutes(date: string): number {
        return (new Date(date).getMinutes());
    }

    private findIntersection(timeRanges: { begin: string; end: string; }[]): any {
        // Sort the array by start time in ascending order
        timeRanges.sort((a, b) => this.compareHour(a.begin, b.begin));

        // Initialize variables for the intersection
        let intersectionStart = timeRanges[0].begin;
        let intersectionEnd = timeRanges[0].end;

        // Traverse the time ranges to find the intersection
        for (let i = 1; i < timeRanges.length; i++) {
            const currentRange = timeRanges[i];
            // If the current range starts after the intersection end, there is no intersection
            if (currentRange.begin > intersectionEnd) return null;

            // Update the intersection start time if the current range has a later start time
            if (currentRange.begin > intersectionStart) intersectionStart = currentRange.begin;

            // Update the intersection end time if the current range has an earlier end time
            if (currentRange.end < intersectionEnd) intersectionEnd = currentRange.end;
        }

        // Final check for intersection
        if (intersectionStart < intersectionEnd) return { begin: intersectionStart, end: intersectionEnd };
        return null;
    }

    private compareHour(hour1: string, hour2: string): number {
        if (hour1 < hour2) return -1;
        else if (hour2 > hour1) return 1;
        return 0;
    }

    private deltaInMinutes(heure1: string, heure2: string): number {
        const date1 = parse(heure1, 'HH:mm', new Date());
        const date2 = parse(heure2, 'HH:mm', new Date());
        const difference = differenceInMinutes(date2, date1);
        return difference;
    }

    public myParseInt(hour: string): number {
        return parseInt(hour.substring(0, 3), 10);
    }

}
